import {
  redirect,
  RedirectType,
  useParams,
  usePathname,
} from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';

const DEBBOUNCE_MS = 25;

const ENTER_KEYWORD = 'Enter';

const MIN_CHARS_FOR_SEARCH = 3;

export const useBlogHeaderSearch = () => {
  const { searchTerm: searchTermParam } = useParams<{ searchTerm?: string }>();

  const _searchTerm = (() => {
    if (!searchTermParam) return '';

    if (typeof searchTermParam === 'string') {
      return decodeURIComponent(searchTermParam);
    }

    if (Array.isArray(searchTermParam)) {
      return searchTermParam[0];
    }

    return '';
  })();

  const [error, setError] = useState<undefined | string>(undefined);
  const [redirectLoading, setRedirectLoading] = useState(false);
  const pathname = usePathname();

  const [searchTerm, setSearchTerm] = useState(_searchTerm);

  const isSearchEligible = useCallback(() => {
    if (
      typeof searchTermParam === 'string' &&
      searchTerm === decodeURIComponent(searchTermParam)
    ) {
      return false;
    }

    if (searchTerm.length <= MIN_CHARS_FOR_SEARCH) {
      setError('Search should contain at least 3 characters.');

      return false;
    }

    return true;
  }, [searchTerm, searchTermParam]);

  const handleSearch = useCallback(() => {
    if (!isSearchEligible()) return;

    const redirectFn = () =>
      redirect(`/blog/search/${searchTerm}`, RedirectType.push);

    setRedirectLoading(true);

    setTimeout(() => {
      redirectFn();

      setRedirectLoading(false);
    }, DEBBOUNCE_MS);
  }, [isSearchEligible, searchTerm]);

  const handleKeyDown = useCallback(
    ({ key }: React.KeyboardEvent<HTMLInputElement>) => {
      if (key !== ENTER_KEYWORD) return;

      handleSearch();
    },
    [handleSearch]
  );

  const isSearchPage = useMemo(
    () => pathname.endsWith(`/search/${searchTermParam}`),
    [pathname, searchTermParam]
  );

  return {
    handleKeyDown,
    handleSearch,
    loading: redirectLoading,
    handleSearchTermChange: setSearchTerm,
    searchTerm,
    isSearchPage,
    error,
  };
};
